/**
 * External systems list page — `GET /external-systems`.
 *
 * An external system is one third-party HTTP API this workspace has registered, plus the API
 * methods generated from it — each method becomes a callable tool for the agents. Deleting a system
 * therefore takes its methods with it, which is what the confirmation has to say.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` for the title and primary action, `CordioAbpPagedDataTable` for the grid, one
 * `DeleteDialog` driven by `useDeleteDialog`, and an `.external-systems-list` BEM block.
 *
 * The endpoint is genuinely paged (`pageNumber`, `pageSize`, `searchTerm`, `isActive` →
 * `PagedResult<ExternalSystemDto>`), so `createCortexPagedDataSource` hands it straight through and
 * the grid owns paging and search outright. That replaced the page's `filters` object, its
 * `totalCount` state and the `useEffect` that refetched on four dependencies.
 *
 * `listVersion` is the one piece of refetch state kept, bumped after a delete.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field              | header      | width | cell type              | filter
 * -------------------|-------------|-------|------------------------|----------------------------
 * name               | Name        | 26%   | text, clickable        | toolbar search -> searchTerm
 * baseUrl            | Base URL    | 30%   | text, 60 + readMore    | toolbar search -> searchTerm
 * authenticationType | Auth        | 16%   | custom (outlined Chip) | —
 * apiMethodCount     | API methods | 12%   | number                 | —
 * isActive           | Status      | 16%   | custom (Active Chip)   | status tabs -> `isActive`
 *
 * Dropped from the pre-migration table: the `description` subtitle under Name (prose that belongs
 * on the detail page — the name and base URL are what identify a system in a list) and the small
 * API icon next to the method count, which added a glyph and no information.
 *
 * Sorting is not offered: `GET /external-systems` accepts no `sortBy`, and a sortable header that
 * reorders nothing is worse than none.
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import {
  externalSystemService,
  type ExternalSystemDto,
  type ExternalSystemFilters,
} from '../services/externalSystemService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import './ExternalSystemsListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey`, AND it builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` /
 * `-false`, so the All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing. `'all'` is a UI concept
 * the API rejects, hence the synthetic {@link STATUS_TAB_PARAM_KEY} that
 * {@link toExternalSystemFilters} resolves to `isActive`.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Turns the grid's query into the `ExternalSystemFilters` shape the service accepts.
 *
 * The synthetic status tab becomes `isActive`, and All becomes "no filter at all" rather than a
 * third state. `sortBy` / `sortDescending` are dropped: the endpoint builds its query string from
 * an explicit allow-list with no sort keys, so passing them would be inert.
 */
function toExternalSystemFilters(query: CortexPagedQuery): ExternalSystemFilters {
  const filters: ExternalSystemFilters = {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
  };

  if (typeof query.searchTerm === 'string' && query.searchTerm.trim() !== '') {
    filters.searchTerm = query.searchTerm;
  }

  const status = query[STATUS_TAB_PARAM_KEY];
  if (status === 'true' || status === 'false') {
    filters.isActive = status === 'true';
  }

  return filters;
}

export const ExternalSystemsListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  /** Bumped after a delete to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<ExternalSystemDto>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ExternalSystemDto>((query) =>
        externalSystemService.getExternalSystems(toExternalSystemFilters(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ExternalSystemDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '26%', clickable: true },
      {
        field: 'baseUrl',
        header: 'Base URL',
        type: 'text',
        width: '30%',
        maxLength: 60,
        showReadMore: true,
      },
      {
        field: 'authenticationType',
        header: 'Auth',
        type: 'custom',
        width: '16%',
        // Outlined rather than filled: the auth scheme is a label, not a state, so it must not
        // compete with the status chip two columns over. The text is the API's own value.
        customCell: (row) =>
          row.authenticationType ? <Chip label={row.authenticationType} variant="outlined" /> : null,
      },
      { field: 'apiMethodCount', header: 'API methods', type: 'number', width: '12%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '16%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  /**
   * Search and status tabs only — no filter popover.
   *
   * WHY: `GET /external-systems` accepts exactly two filters, `searchTerm` and `isActive`, and both
   * are already on screen (the search box and the tabs). A popover could only duplicate a tab or
   * send a key the endpoint's allow-list discards, which is a filter that looks like it works and
   * does nothing. So the grid renders no `cordio-datatable-filter-trigger` here.
   */
  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search external systems by name or URL' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'External system status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ExternalSystemDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View details',
        onClick: (row) => navigate(`${ROUTES.EXTERNAL_SYSTEMS}/${row.id}`),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit external system',
        onClick: (row) => navigate(ROUTES.EXTERNAL_SYSTEM_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete external system',
        className: 'external-systems-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await externalSystemService.deleteExternalSystem(pendingItem.id);
    addNotification('External system deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  return (
    <div className="external-systems-list" data-testid="external-system-list-page">
      <ShellHeader
        title="External Systems"
        description="Third-party APIs this workspace has registered. Each API method becomes a tool the agents can call."
        data-testid="external-system-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-external-system-btn"
            onClick={() => navigate(ROUTES.EXTERNAL_SYSTEM_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create External System
          </Button>
        }
      />

      <div data-testid="external-system-list-grid">
        <CordioAbpPagedDataTable<ExternalSystemDto>
          className="external-systems-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.EXTERNAL_SYSTEMS}/${row.id}`)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete external system?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Its API methods are deleted with it, so every agent tool generated from them stops working and the integrations that depend on it fail. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
